import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS, CMS_BASE_URL } from '../constants/api';
import { ProductModal } from './Products';
import { ShoppingCart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';

interface Category {
  id: string;
  label: string;
  order: number;
}

interface Product {
  id: number;
  title: string;
  category: string;
  image: string;
  memberPrice: string;
  regularPrice: string;
  qty: string;
  description: string;
  featured: number;
  categoryOrder: number;
}

interface ProductsData {
  [key: string]: Product[];
}

interface ProductCardProps {
  product: Product;
  onAddToCart: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToCart }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const productRect = e.currentTarget.getBoundingClientRect();
    const cartIcon = document.querySelector('.cart-icon');
    const cartRect = cartIcon?.getBoundingClientRect();

    if (cartRect) {
      const floatingEl = document.createElement('div');
      floatingEl.className = 'floating-cart-item fixed z-[100]';
      floatingEl.style.cssText = `
        top: ${productRect.top}px;
        left: ${productRect.left}px;
        width: 100px;
        height: 100px;
        background-image: url(${product.image});
        background-size: cover;
        background-position: center;
        border-radius: 50%;
        pointer-events: none;
        transition: all 1s cubic-bezier(0.23, 1, 0.32, 1);
        opacity: 1;
      `;

      document.body.appendChild(floatingEl);

      // Trigger animation
      setTimeout(() => {
        floatingEl.style.transform = 'scale(0.3)';
        floatingEl.style.top = `${cartRect.top + cartRect.height/2}px`;
        floatingEl.style.left = `${cartRect.left + cartRect.width/2}px`;
        floatingEl.style.opacity = '0';
      }, 50);

      // Clean up
      setTimeout(() => {
        document.body.removeChild(floatingEl);
        onAddToCart(product);
      }, 1000);
    }
  };

  return (
    <>
      <div className="product-card bg-white rounded-lg overflow-hidden transition-transform hover:scale-105 p-4 relative">
        {/* Add animation overlay */}
        {isAnimating && (
          <div className="absolute inset-0 bg-black/5 z-10 animate-ping" />
        )}
        
        <div 
          onClick={() => setIsModalOpen(true)}
          className="cursor-pointer"
        >
          <img 
            src={product.image} 
            alt={product.title} 
            className="product-image w-full h-48 object-cover" 
          />
        </div>
        <h3 
          onClick={() => setIsModalOpen(true)}
          className="text-xl font-bold text-gray-900 mt-2 text-center cursor-pointer hover:text-custom-green transition-colors"
        >
          {product.title}
        </h3>
        <p className="text-gray-600 text-sm text-center mt-1">{product.qty}</p>
        <div className="space-y-0.5 text-center mt-1">
          <p>
            <span className="text-black">Regular price:</span>{' '}
            <span className="text-red-500 font-bold">{product.regularPrice}</span>{' '}
            <sup className="text-[10px] font-bold text-red-500">BOX</sup>
          </p>
          <p>
            <span className="text-black">Member price:</span>{' '}
            <span className="text-custom-green font-bold">{product.memberPrice}</span>{' '}
            <sup className="text-[10px] font-bold text-custom-green">BOX</sup>
          </p>
        </div>
        <div className="mt-4 space-y-2">
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setIsModalOpen(true);
            }}
            className="w-full bg-[#e81217] text-white py-2.5 rounded-md font-medium transition-all hover:bg-[#d11015] active:transform active:scale-[0.98]"
          >
            Learn More
          </button>
          <button
            onClick={handleAddToCart}
            className="w-full bg-black text-white py-2.5 rounded-md font-medium transition-all hover:bg-gray-900 active:transform active:scale-[0.98] flex items-center justify-center"
          >
            <ShoppingCart className="w-4 h-4 mr-2" />
            <span>Add to Cart</span>
          </button>
        </div>
      </div>

      <ProductModal
        product={product}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

interface FilterGridProps {
  onCategoryClick: (category: string) => void;
}

const FilterGrid: React.FC<FilterGridProps> = ({ onCategoryClick }) => {
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [productsData, setProductsData] = useState<ProductsData>({});
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(API_ENDPOINTS.HOME_PRODUCTS);
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        const data = await response.json();
        // Create a map to store unique categories with their order
        const categoryMap = new Map<string, Category>();
        const transformedData: ProductsData = {};

        // First pass: gather unique categories
        data.forEach((item: any) => {
          const categoryId = item.category.toLowerCase();
          if (!categoryMap.has(categoryId)) {
            categoryMap.set(categoryId, {
              id: categoryId,
              label: item.category,
              order: item.categoryOrder
            });
            transformedData[categoryId] = [];
          }
        });

        // Convert categories to sorted array
        const sortedCategories = Array.from(categoryMap.values())
          .sort((a, b) => a.order - b.order);
        
        setCategories(sortedCategories);
        if (!activeCategory && sortedCategories.length > 0) {
          setActiveCategory(sortedCategories[0].id);
        }

        // Second pass: organize products by category
        data.forEach((item: any) => {
          const categoryId = item.category.toLowerCase();
          const product: Product = {
            id: item.featuredOrder,
            title: item.name,
            category: categoryId,
            image: item.imageUrl.startsWith('http') ? item.imageUrl : `${CMS_BASE_URL}${item.imageUrl}`,
            memberPrice: `${item.memberPrice} $`,
            regularPrice: `${item.regularPrice} $`,
            qty: item.quantity,
            description: item.description,
            featured: 1,
            categoryOrder: item.categoryOrder
          };
          
          transformedData[categoryId].push(product);
        });

        // Sort products within each category
        Object.keys(transformedData).forEach(category => {
          transformedData[category].sort((a, b) => (a.id || 0) - (b.id || 0));
        });

        setProductsData(transformedData);
        setProductsData(transformedData);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred while fetching products');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = (productsData[activeCategory] || [])
    .filter(product => product.featured === 1)
    .slice(0, 8);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    onCategoryClick(category);
  };

  const navigate = useNavigate();
  
  const handleViewMore = () => {
    navigate(`/${activeCategory}`);
  };

  const handleAddToCart = (product: Product) => {
    addToCart({
      id: product.id,
      title: product.title,
      price: product.regularPrice,
      regularPrice: product.regularPrice,
      image: product.image,
      category: product.category,
      quantity: 1
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-8"></div>
            
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {[...Array(5)].map((_, index) => (
                <div key={index} className="h-10 w-24 bg-gray-200 rounded-full"></div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(8)].map((_, index) => (
                <div key={index} className="bg-white rounded-lg p-4">
                  <div className="h-48 bg-gray-200 rounded mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-red-500">
            <h2 className="text-2xl font-bold mb-4">Error Loading Products</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Products</h2>
          <p className="text-lg text-gray-600">
            Browse our selection of premium quality meats
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => handleCategoryChange(category.id)}
              className={`px-8 py-2 rounded-full transition-colors font-bold ${
                activeCategory === category.id
                  ? 'bg-custom-red text-white border-none'
                  : 'bg-white text-black border border-black hover:border-gray-300 hover:text-gray-600'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
          ))}
        </div>

        {filteredProducts.length > 0 && (
          <div className="mt-12 text-center">
            <button
              onClick={handleViewMore}
              className="bg-white text-custom-green px-8 py-3 rounded-full border-3 border-custom-green hover:bg-gray-50 transition-colors font-bold"
            >
              View More {activeCategory} Products
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterGrid;
